import { Box, Text } from '@chakra-ui/layout'
import { Button, Input, useToast } from '@chakra-ui/react'
import { Layout } from 'components/atoms'
import { Icon } from 'components/atoms/Icon'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { FormActions } from 'pages/Contracts/Components/StepsForm/styles'

import React, { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import InstitutionsService from 'services/institutions'
import { FormFileDataProps } from './interface'

interface SendingFilesProps {
    onClose: () => void
    onSubmit?: () => void
    id: string
    typeDocument: number
}

const SendingFiles: React.FC<SendingFilesProps> = ({
    onSubmit,
    onClose,
    typeDocument,
    id: idInstitution
}) => {
    const { id = '' } = useParams()

    const [fileData, setFileData] = useState<FormFileDataProps>()
    const toast = useToast()
    const toastSucess = useToast({ variant: 'solid-info' })
    const [loadingRequest, setLoadingRequest] = useState<boolean>(false)

    const ref = useRef<HTMLInputElement>(null)

    const sendComputerFile = () => {
        ref.current?.click()
    }

    const deleteModalRef = useRef<ModalBuildHandle>(null)

    const handleOpenDeleteModal = (): void => {
        deleteModalRef.current?.onOpen()
    }

    const handleCloseDeleteModal = (): void => {
        deleteModalRef.current?.onClose()
    }

    const handleRemoveFile = () => {
        setFileData({} as FormFileDataProps)
        handleCloseDeleteModal()
    }

    const fetchPostAttachment = async (body: FormFileDataProps) => {
        try {
            await InstitutionsService.postUploadAttachmentByInstitutions(
                idInstitution,
                body
            )

            toastSucess({
                title: 'Upload de anexo com sucesso!',
                description: 'Acesse o seu anexo na área de anexos.',
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })

            onClose()
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Tipo de arquivo não permitido',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setFileData({} as FormFileDataProps)
            setLoadingRequest(false)
        }
    }

    const handleSubmit = async (id: string, data: FormFileDataProps) => {
        setLoadingRequest(true)
        await fetchPostAttachment(data)
        onSubmit && onSubmit()
    }

    const convertToBase64 = (data: File) => {
        const reader = new FileReader()

        reader.readAsDataURL(data)

        reader.onload = () => {
            const base64 =
                reader.result &&
                reader.result.toString().split(',', 2)[1]?.length / 3

            const sizeInBytes =
                (4 * Math.ceil(base64 as number) * 0.5624896334383812) / 1000000

            const sizeInKb = parseFloat(sizeInBytes.toFixed(3))

            if (sizeInKb > 5) {
                toast({
                    title: 'Sentimos muito!',
                    description:
                        'O arquivo deve possuir tamanho máximo de 5 Mb (cinco megabytes), nos formatos JPG ou PDF.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            } else {
                setFileData({
                    base64:
                        reader.result &&
                        reader.result.toString().split(',', 2)[1],
                    nome: data.name,
                    tipoDocumento: typeDocument
                })
            }
        }
    }

    const formatIsValid = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0]
        const fileName = file && file.name
        const acceptedFormats = ['jpg', 'pdf']

        const fileExt =
            fileName && fileName.substring(fileName.lastIndexOf('.') + 1)

        return fileExt && acceptedFormats.indexOf(fileExt) !== -1
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!typeDocument) {
            toast({
                title: 'Tipo de Documento Obrigatório',
                description:
                    'Por favor, selecione o tipo de documento antes de inserir o arquivo.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })

            return
        }

        if (formatIsValid(event)) {
            const fileObj = event.target.files && event.target.files[0]

            event.target.value = ''

            if (fileObj && fileObj.size / 1024 <= 5120) {
                convertToBase64(fileObj)
            }
        } else {
            toast({
                title: 'Sentimos muito!',
                description: 'Tipo de arquivo não permitido',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        }
    }

    return (
        <Layout type="container" px={8} display="grid" gap={6} padding="0">
            <Box
                textAlign="center"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap={2}
                height="230px"
                position="relative"
            >
                {!fileData?.nome ? (
                    <Box
                        data-testid="boxSendComputerFile"
                        textAlign="center"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        gap={2}
                        zIndex={12}
                        onClick={sendComputerFile}
                        cursor="pointer"
                    >
                        <Text whiteSpace="pre" color="black" fontWeight="700">
                            Nenhum anexo selecionado.
                        </Text>
                        <Text
                            whiteSpace="pre"
                            color="black"
                            fontWeight="700"
                            textDecoration="underline"
                            ref={ref}
                        >
                            Faça upload agora mesmo.
                        </Text>
                        <Input
                            data-testid="input-file"
                            ref={ref}
                            type="file"
                            display={'none'}
                            accept=".jpg,.pdf"
                            onChange={handleFileChange}
                        />
                        <Text
                            whiteSpace="pre"
                            color="black"
                            maxWidth="292px"
                            fontSize={12}
                        >
                            O arquivo deve possuir tamanho máximo de 5 Mb <br />{' '}
                            (cinco megabytes), nos formatos JPG ou PDF.
                        </Text>
                        <Box position="absolute" zIndex="-1" cursor="pointer">
                            <Icon name="file-alt-white" size={124} />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        bg="grayscale.50"
                        width="100%"
                        height="52px"
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        flexDirection="row"
                        justifyContent="space-between"
                        padding="0 30px"
                        top="0px"
                        mt="16px"
                    >
                        <Text
                            whiteSpace="pre"
                            color="grayscale.800"
                            fontSize={14}
                        >
                            {fileData?.nome}
                        </Text>
                        <Box display="flex" flexDir="row">
                            <Icon
                                data-testid="removeFile"
                                name="delet"
                                onClick={handleOpenDeleteModal}
                                cursor="pointer"
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            <Layout
                type="row"
                gutter={{ y: 1.5 }}
                data-testid="form-fields"
            ></Layout>
            <FormActions>
                <Button
                    data-testid="form-action-cancel"
                    colorScheme="primary"
                    variant="outline"
                    onClick={onClose}
                >
                    Cancelar
                </Button>
                <Button
                    data-testid="form-action-confirm"
                    colorScheme="primary"
                    isDisabled={!fileData?.nome || !typeDocument}
                    isLoading={loadingRequest}
                    onClick={() =>
                        handleSubmit(id, fileData as FormFileDataProps)
                    }
                >
                    Finalizar
                </Button>
            </FormActions>
            <ModalBuild
                ref={deleteModalRef}
                closeOnOverlayClick={false}
                title="Confirmação de exclusão"
                size="md"
                actions={{
                    position: 'flex-end',
                    childrens: [
                        {
                            text: 'Cancelar',
                            handle: 'cancel',
                            variant: 'outline'
                        },
                        {
                            text: 'Deletar',
                            handle: 'confirm',
                            colorScheme: 'red'
                        }
                    ]
                }}
                onConfirm={handleRemoveFile}
                onCancel={handleCloseDeleteModal}
            >
                <Text px={6} py={4}>
                    Você realmente deseja deletar? Uma vez deletado, não será
                    possível voltar atrás.
                </Text>
            </ModalBuild>
        </Layout>
    )
}

export default SendingFiles
